import React from 'react';
import { Grid, Stack, Typography } from '@mui/material';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ILegalEntityGroupResponse } from 'models/legalEntity';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'hooks/useQuery';
import { useTranslations } from 'hooks/useTranslations';
import { EPermission } from 'models/permissions';
import { getLegalEntityType } from 'components/organisms/RegisteredFirearmDetails';
import { StyledLegalLink } from 'components/atoms/StyledLegalLink';
import { EState, statesWithoutMovedTransaction } from 'utils/transactionUtils';
import { Asset } from 'utils/assets';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import { usePermission } from 'hooks/usePermission';

interface ILegalEntityDetailsProps {
  legalEntityGroup: ILegalEntityGroupResponse;
  hideManagement: boolean;
}

const LegalEntityGroupDetails = ({
  legalEntityGroup,
  hideManagement,
}: ILegalEntityDetailsProps) => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();

  return (
    <div>
      {legalEntityGroup && (
        <DetailsTemplate
          title={legalEntityGroup.name}
          pageType={EDetailsPageTypes.view}
          deletePermissions={[EPermission.MANAGE_GROUPS_LE]}
          editPermissions={[EPermission.MANAGE_GROUPS_LE]}
          redirectToEditView={
            !hideManagement
              ? () =>
                  navigate(
                    `${ERouteLinks.LegalEntityEditGroup.replace(':id', id).replace(
                      ':groupId',
                      legalEntityGroup.uniqueId,
                    )}?type=${type}`,
                  )
              : undefined
          }
          redirectToView={
            !hideManagement
              ? () =>
                  navigate(
                    `${ERouteLinks.LegalEntityDeleteGroup.replace(':id', id).replace(
                      ':groupId',
                      legalEntityGroup.uniqueId,
                    )}?type=${type}&expand=groups`,
                  )
              : undefined
          }
          redirectToMovedTransaction={
            !statesWithoutMovedTransaction.includes(
              legalEntityGroup.stateUniqueId as EState,
            ) && !legalEntityGroup.hasDifferentStates
              ? () => {
                  navigate(ERouteLinks.MoveTransaction, {
                    state: {
                      assetUniqueId: legalEntityGroup.uniqueId,
                      asset: Asset.FIREARM,
                      isGroup: true,
                      groupKeeperOrOwnerUniqueId:
                        legalEntityGroup.keeperUniqueId || legalEntityGroup.ownerUniqueId,
                      searchAddressesFor:
                        legalEntityGroup.keeperUniqueId || legalEntityGroup.ownerUniqueId,
                      navigateBack: `${ERouteLinks.ViewRegisteredFirearm.replace(
                        ':id',
                        legalEntityGroup.uniqueId as string,
                      )}`,
                      breadcrumbs: [
                        {
                          label: t('module.legalEntity.name'),
                          route: ERouteLinks.LegalEntities,
                        },
                        {
                          label:
                            type === ELegalEntityTypes.individual
                              ? t('legalEntities.individual.accordion.label')
                              : t('legalEntities.organization.accordion.label'),
                          route: `${ERouteLinks.LegalEntities}?expand=${
                            type === ELegalEntityTypes.individual
                              ? ELegalEntityTypes.individual
                              : ELegalEntityTypes.organisation
                          }`,
                        },
                        {
                          label: t('legalEntityDetails.legalEntityDetails.text'),
                          route: `${ERouteLinks.ViewLegalEntity.replace(
                            ':id',
                            id as string,
                          )}?type=${type}&expand=groups`,
                        },
                        {
                          label: t('groupDetails.header'),
                          route: `${ERouteLinks.ViewLegalEntityGroup.replace(
                            ':id',
                            id as string,
                          ).replace(':groupId', legalEntityGroup.uniqueId)}`,
                        },
                        t('transactions.movedTransaction.breadcrumb.label'),
                      ],
                    },
                  });
                }
              : undefined
          }
          hideMovedTransaction={
            (statesWithoutMovedTransaction.includes(
              legalEntityGroup.stateUniqueId as EState,
            ) &&
              !legalEntityGroup.hasDifferentStates) ||
            !hasPermissions(EPermission.ADD_TRANSACTION) ||
            hideManagement
          }
          changeLocationButtonVisibility={legalEntityGroup.firearmAmount > 0}
        >
          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('groupDetails.owner.label')}
              </Typography>
              <Typography variant="body2" id="owner">
                <StyledLegalLink
                  legalId={legalEntityGroup.ownerUniqueId}
                  legalType={getLegalEntityType(legalEntityGroup.ownerType)}
                  labelText={legalEntityGroup.owner}
                  legalVisibilityUniqueId={legalEntityGroup.ownerVisibilityUniqueId}
                />
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('groupDetails.keeper.label')}
              </Typography>
              <Typography variant="body2" id="keeper">
                <StyledLegalLink
                  legalId={legalEntityGroup.keeperUniqueId}
                  legalType={getLegalEntityType(legalEntityGroup.keeperType)}
                  labelText={legalEntityGroup.keeper}
                  legalVisibilityUniqueId={legalEntityGroup.keeperVisibilityUniqueId}
                />
              </Typography>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('groupDetails.totalFirearmCount.label')}
              </Typography>
              <Typography variant="body2" id="totalFirearmCount">
                {legalEntityGroup.firearmAmount}
              </Typography>
            </Stack>
          </Grid>
        </DetailsTemplate>
      )}
    </div>
  );
};

export { LegalEntityGroupDetails };
